import React, { useState, useEffect } from 'react';
import { useAuth0 } from "@auth0/auth0-react";

interface UserProfile {
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  bio: string;
  interests: string[];
  shoeSize: string;
  preferredBrands: string[];
}

const MyProfile: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const [profile, setProfile] = useState<UserProfile>({
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    bio: '',
    interests: [],
    shoeSize: '',
    preferredBrands: []
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Available options
  const availableInterests = [
    'Running', 'Basketball', 'Soccer', 'Tennis', 'Hiking', 'Fashion', 
    'Skateboarding', 'Casual Wear', 'Work Shoes', 'Athletic Performance'
  ];
  const availableBrands = [
    'Nike', 'Adidas', 'Puma', 'New Balance', 'Converse', 'Vans', 
    'Under Armour', 'ASICS', 'Reebok', 'Jordan'
  ];

  // Load profile from localStorage on component mount
  useEffect(() => {
    if (isAuthenticated && user?.sub) {
      // Pre-populate with Auth0 user data
      setProfile(prev => ({
        ...prev,
        firstName: user.given_name || '',
        lastName: user.family_name || '',
      }));

      // Load saved profile data
      const savedProfile = localStorage.getItem(`profile_${user.sub}`);
      if (savedProfile) {
        try {
          const parsed = JSON.parse(savedProfile);
          setProfile(prev => ({
            ...prev,
            ...parsed
          }));
        } catch (error) {
          console.error('Error loading profile:', error);
        }
      }
    }
  }, [isAuthenticated, user]);

  const handleInputChange = (field: keyof UserProfile, value: string) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field: 'interests' | 'preferredBrands', value: string, checked: boolean) => {
    setProfile(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }));
  };

  const saveProfile = async () => {
    if (!isAuthenticated || !user?.sub) return;

    setIsSaving(true);
    setSaveMessage('');

    try {
      // Save to localStorage (in a real app, you'd save to your backend)
      localStorage.setItem(`profile_${user.sub}`, JSON.stringify(profile));
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSaveMessage('Profile saved successfully!');
      setIsEditing(false);
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setSaveMessage('Error saving profile. Please try again.');
      console.error('Error saving profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    // Reload from localStorage to cancel changes
    if (user?.sub) {
      const savedProfile = localStorage.getItem(`profile_${user.sub}`);
      if (savedProfile) {
        try {
          const parsed = JSON.parse(savedProfile);
          setProfile(prev => ({
            ...prev,
            ...parsed
          }));
        } catch (error) {
          console.error('Error reloading profile:', error);
        }
      }
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-start', 
        alignItems: 'flex-start', 
        height: '400px',
        fontSize: '1.2rem',
        color: '#666',
        padding: '40px 20px'
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={{ 
        textAlign: 'left', 
        padding: '60px 20px',
        color: '#666',
        fontSize: '1.1rem'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🔒</div>
        <div>Please log in to access your profile.</div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '40px 20px', 
      maxWidth: '800px', 
      margin: '0 auto', 
      fontFamily: 'Arial, sans-serif',
      textAlign: 'left'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: window.innerWidth < 768 ? '24px' : '40px',
        flexWrap: window.innerWidth < 480 ? 'wrap' : 'nowrap',
        gap: window.innerWidth < 480 ? '16px' : '0'
      }}>
        <h1 style={{ 
          textAlign: 'left', 
          color: '#333',
          fontSize: window.innerWidth < 480 ? '1.5rem' : window.innerWidth < 768 ? '2rem' : '2.5rem',
          fontWeight: 'bold',
          margin: 0
        }}>
          My Profile
        </h1>

        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            style={{
              padding: window.innerWidth < 480 ? '8px 16px' : '12px 24px',
              backgroundColor: '#2c5aa0',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: window.innerWidth < 480 ? '0.875rem' : '1rem',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              whiteSpace: 'nowrap'
            }}
          >
            Edit Profile
          </button>
        ) : (
          <div style={{ display: 'flex', gap: window.innerWidth < 480 ? '8px' : '12px' }}>
            <button
              onClick={saveProfile}
              disabled={isSaving}
              style={{
                padding: window.innerWidth < 480 ? '8px 16px' : '12px 24px',
                backgroundColor: isSaving ? '#ccc' : '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: window.innerWidth < 480 ? '0.875rem' : '1rem',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={cancelEdit}
              style={{
                padding: window.innerWidth < 480 ? '8px 16px' : '12px 24px',
                backgroundColor: '#6b7280',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: window.innerWidth < 480 ? '0.875rem' : '1rem',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0'
      }}>
        {/* Profile Picture and Basic Info */}
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          marginBottom: '40px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#2c5aa0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '2rem',
            fontWeight: 'bold',
            marginRight: '24px',
            backgroundImage: user?.picture ? `url(${user.picture})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}>
            {!user?.picture && (user?.name?.charAt(0) || user?.email?.charAt(0) || 'U')}
          </div>
          <div>
            <h2 style={{ 
              color: '#2c5aa0', 
              margin: '0 0 8px 0', 
              fontSize: '1.5rem',
              fontWeight: '600'
            }}>
              {user?.name || `${profile.firstName} ${profile.lastName}`.trim() || 'User'}
            </h2>
            <div style={{ color: '#666', fontSize: '1rem' }}>
              {user?.email}
            </div>
            <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '4px' }}>
              Member since {new Date(user?.updated_at || Date.now()).toLocaleDateString()}
            </div>
          </div>
        </div>

        {saveMessage && (
          <div style={{
            padding: '12px',
            marginBottom: '20px',
            backgroundColor: saveMessage.includes('Error') ? '#fef2f2' : '#f0fdf4',
            color: saveMessage.includes('Error') ? '#dc3545' : '#28a745',
            border: `1px solid ${saveMessage.includes('Error') ? '#fecaca' : '#bbf7d0'}`,
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '500'
          }}>
            {saveMessage}
          </div>
        )}

        {/* Personal Information */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ 
            color: '#2c5aa0', 
            marginBottom: '20px', 
            fontSize: '1.3rem',
            fontWeight: '600'
          }}>
            Personal Information
          </h2>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '16px',
            marginBottom: '20px'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px',
                fontSize: '1rem',
                color: '#333',
                fontWeight: '500'
              }}>
                First Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={profile.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '1rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#fff'
                  }}
                />
              ) : (
                <div style={{ 
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  color: profile.firstName ? '#333' : '#999'
                }}>
                  {profile.firstName || 'Not provided'}
                </div>
              )}
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px',
                fontSize: '1rem',
                color: '#333',
                fontWeight: '500'
              }}>
                Last Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={profile.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '1rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#fff'
                  }}
                />
              ) : (
                <div style={{ 
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  color: profile.lastName ? '#333' : '#999'
                }}>
                  {profile.lastName || 'Not provided'}
                </div>
              )}
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px',
                fontSize: '1rem',
                color: '#333',
                fontWeight: '500'
              }}>
                Phone
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '1rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#fff'
                  }}
                />
              ) : (
                <div style={{ 
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  color: profile.phone ? '#333' : '#999'
                }}>
                  {profile.phone || 'Not provided'}
                </div>
              )}
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px',
                fontSize: '1rem',
                color: '#333',
                fontWeight: '500'
              }}>
                Shoe Size
              </label>
              {isEditing ? (
                <select
                  value={profile.shoeSize}
                  onChange={(e) => handleInputChange('shoeSize', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '1rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#fff'
                  }}
                >
                  <option value="">Select size</option>
                  {Array.from({length: 15}, (_, i) => i + 5).map(size => (
                    <option key={size} value={size.toString()}>{size}</option>
                  ))}
                </select>
              ) : (
                <div style={{ 
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  color: profile.shoeSize ? '#333' : '#999'
                }}>
                  {profile.shoeSize || 'Not provided'}
                </div>
              )}
            </div>
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px',
              fontSize: '1rem',
              color: '#333',
              fontWeight: '500'
            }}>
              Bio
            </label>
            {isEditing ? (
              <textarea
                value={profile.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '1rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  resize: 'vertical'
                }}
                placeholder="Tell us about yourself..."
              />
            ) : (
              <div style={{ 
                padding: '8px 12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                color: profile.bio ? '#333' : '#999',
                minHeight: '60px'
              }}>
                {profile.bio || 'No bio provided'}
              </div>
            )}
          </div>
        </div>

        {/* Interests */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ 
            color: '#2c5aa0', 
            marginBottom: '20px', 
            fontSize: '1.3rem',
            fontWeight: '600'
          }}>
            Interests
          </h2>
          
          {isEditing ? (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '12px'
            }}>
              {availableInterests.map(interest => (
                <label key={interest} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  fontSize: '1rem',
                  color: '#333'
                }}>
                  <input
                    type="checkbox"
                    checked={profile.interests.includes(interest)}
                    onChange={(e) => handleArrayChange('interests', interest, e.target.checked)}
                    style={{ marginRight: '8px', transform: 'scale(1.1)' }}
                  />
                  {interest}
                </label>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {profile.interests.length > 0 ? (
                profile.interests.map(interest => (
                  <span
                    key={interest}
                    style={{
                      backgroundColor: '#2c5aa0',
                      color: '#fff',
                      padding: '6px 12px',
                      borderRadius: '16px',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}
                  >
                    {interest}
                  </span>
                ))
              ) : (
                <div style={{ color: '#999', fontStyle: 'italic' }}>
                  No interests selected
                </div>
              )}
            </div>
          )}
        </div>

        {/* Preferred Brands */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ 
            color: '#2c5aa0', 
            marginBottom: '20px', 
            fontSize: '1.3rem',
            fontWeight: '600'
          }}>
            Preferred Brands
          </h2>
          
          {isEditing ? (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
              gap: '12px'
            }}>
              {availableBrands.map(brand => (
                <label key={brand} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  fontSize: '1rem',
                  color: '#333'
                }}>
                  <input
                    type="checkbox"
                    checked={profile.preferredBrands.includes(brand)}
                    onChange={(e) => handleArrayChange('preferredBrands', brand, e.target.checked)}
                    style={{ marginRight: '8px', transform: 'scale(1.1)' }}
                  />
                  {brand}
                </label>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {profile.preferredBrands.length > 0 ? (
                profile.preferredBrands.map(brand => (
                  <span
                    key={brand}
                    style={{
                      backgroundColor: '#f0f0f0',
                      color: '#666',
                      padding: '6px 12px',
                      borderRadius: '16px',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    {brand}
                  </span>
                ))
              ) : (
                <div style={{ color: '#999', fontStyle: 'italic' }}>
                  No preferred brands selected
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyProfile;